import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { api, getToken } from '../../lib/api';
import { textoDeError } from '../Dialogo';
import { Recortador, abrirArchivo, abrirBlob, generarJpeg, reducirOriginal, recInicial, recDeNormal, cajaDe, normalDe } from '../RecorteImagen';
import type { Fuente, Rec, Normal, VistaImagen } from '../RecorteImagen';
import { ESPACIOS } from '../../lib/promos';
import type { Espacio } from '../../lib/promos';

/**
 * LAS IMÁGENES DE UNA PROMOCIÓN (paso 5 del editor; PROMOS-PRO §3). UNA foto principal sirve para los tres lugares, cada
 * uno con su marco: la tarjeta de la lista (16:9), la promo abierta (4:5) y la notificación (2:1). Cada espacio puede
 * llevar "otra imagen aquí". "Usar esta imagen" (o Guardar) genera los JPEG del tamaño de cada espacio, los sube con el
 * id de la promo, y guarda el recorte (0..1) y la foto original reducida: al volver a abrir, cada marco queda donde se dejó.
 *
 * El estado vive en el editor (`EstadoImagenes`): así la vista previa del teléfono enseña el recorte EN VIVO.
 */
export const ORDEN_ESPACIOS: Espacio[] = ['tarjeta', 'detalle', 'aviso'];
const RECOMENDADO = 2160;
export type Subida = { ruta: string; url: string | null; ancho: number; alto: number };
/** Una foto (la principal o la propia de un espacio): el archivo abierto (si ya se abrió) y lo subido (si ya se subió). */
export type Foto = { fuente: Fuente | null; subida: Subida | null; cargando?: boolean; error?: string | null };
export type EspacioImg = {
  propia: Foto | null;
  /** El marco de ahora, en pixeles de su foto (null mientras la foto no se abre). */
  rec: Rec | null;
  /** El recorte guardado (para volver a poner el marco cuando la foto se abra). */
  normal: Normal | null;
  /** Lo que está subido de este espacio, de qué foto salió (`de` = su ruta) y con qué recorte. */
  subida: (Subida & { normal: Normal; de: string | null }) | null;
};
export type EstadoImagenes = { principal: Foto | null; tarjeta: EspacioImg; detalle: EspacioImg; aviso: EspacioImg };
const espacioVacio = (): EspacioImg => ({ propia: null, rec: null, normal: null, subida: null });
export const imagenesVacias = (): EstadoImagenes => ({ principal: null, tarjeta: espacioVacio(), detalle: espacioVacio(), aviso: espacioVacio() });

/** El estado a partir de lo guardado en `hub_promos.imagenes` (+ las URLs firmadas que manda el API). */
export function imagenesDesdePromo(im: any, urls: Record<string, string | null> | null | undefined): EstadoImagenes {
  const o = im && typeof im === 'object' ? im : {}; const u = urls || {};
  const subidaDe = (k: string): Subida | null => (o[k]?.ruta ? { ruta: o[k].ruta, url: u[k] || null, ancho: Number(o[k].ancho) || 0, alto: Number(o[k].alto) || 0 } : null);
  const principal = subidaDe('original');
  const e: EstadoImagenes = { principal: principal ? { fuente: null, subida: principal } : null, tarjeta: espacioVacio(), detalle: espacioVacio(), aviso: espacioVacio() };
  for (const k of ORDEN_ESPACIOS) {
    const propia = subidaDe(`original_${k}`);
    const s = subidaDe(k);
    const normal = o[k]?.recorte && o[k].recorte.w > 0 ? (o[k].recorte as Normal) : null;
    e[k] = { propia: propia ? { fuente: null, subida: propia } : null, rec: null, normal,
      subida: s ? { ...s, normal: normal || { x: 0, y: 0, w: 1, h: 1 }, de: (o[k]?.fuente === 'propia' ? propia?.ruta : principal?.ruta) || null } : null };
  }
  return e;
}

const fotoDe = (e: EstadoImagenes, k: Espacio): Foto | null => e[k].propia || e.principal;
const dist = (a: Normal, b: Normal) => Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y), Math.abs(a.w - b.w), Math.abs(a.h - b.h));
/** ¿Este espacio tiene un recorte que todavía no se sube? */
export function pendiente(e: EstadoImagenes, k: Espacio): boolean {
  const f = fotoDe(e, k); const s = e[k];
  if (!f?.fuente || !s.rec) return false;
  const n = normalDe(f.fuente, cajaDe(f.fuente, s.rec, ESPACIOS[k].aspecto));
  return !s.subida || s.subida.de !== (f.subida?.ruta || null) || dist(n, s.subida.normal) > 1e-3;
}
export const hayPendientes = (e: EstadoImagenes, conAviso: boolean) =>
  !!(e.principal?.fuente && !e.principal.subida) || ORDEN_ESPACIOS.some((k) => (k !== 'aviso' || conAviso) && (pendiente(e, k) || !!(e[k].propia?.fuente && !e[k].propia!.subida)));
/** Lo que enseña la vista previa en cada espacio: el recorte en vivo o lo ya subido. */
export function vistasDe(e: EstadoImagenes): Record<Espacio, VistaImagen> {
  const v = (k: Espacio): VistaImagen => {
    const f = fotoDe(e, k); const s = e[k];
    if (f?.fuente && s.rec) return { tipo: 'recorte', fuente: f.fuente, caja: cajaDe(f.fuente, s.rec, ESPACIOS[k].aspecto) };
    if (s.subida?.url) return { tipo: 'url', url: s.subida.url };
    return null;
  };
  return { tarjeta: v('tarjeta'), detalle: v('detalle'), aviso: v('aviso') };
}
/** El JSON de `imagenes` que se guarda (sólo lo subido) y la ruta de la notificación (para el aviso). */
export function imagenesJson(e: EstadoImagenes): { imagenes: Record<string, any>; aviso: string | null } {
  const out: Record<string, any> = {};
  if (e.principal?.subida) out.original = { ruta: e.principal.subida.ruta, ancho: e.principal.subida.ancho, alto: e.principal.subida.alto };
  for (const k of ORDEN_ESPACIOS) {
    const s = e[k];
    if (s.propia?.subida) out[`original_${k}`] = { ruta: s.propia.subida.ruta, ancho: s.propia.subida.ancho, alto: s.propia.subida.alto };
    if (s.subida) out[k] = { ruta: s.subida.ruta, ancho: s.subida.ancho, alto: s.subida.alto, recorte: s.subida.normal, fuente: s.propia ? 'propia' : 'original' };
  }
  return { imagenes: out, aviso: e.aviso.subida?.ruta || null };
}

/** Trae una imagen guardada por la API del mismo sitio (un blob local no "ensucia" el canvas al recortar). */
async function traerFoto(ruta: string, nombre: string): Promise<Fuente> {
  const t = getToken();
  const r = await fetch(`/api/promos/imagen?ruta=${encodeURIComponent(ruta)}`, { headers: t ? { 'x-auth-token': t, Authorization: `Bearer ${t}` } : {} });
  if (!r.ok) { let m = ''; try { m = (await r.json()).error; } catch { /* sin cuerpo */ } throw new Error(m || `No se pudo abrir la foto guardada (HTTP ${r.status}).`); }
  return abrirBlob(await r.blob(), nombre);
}

/**
 * Sube lo que falte: las fotos originales (reducidas) y el recorte de cada espacio que cambió. La notificación sólo si
 * el aviso al empezar va prendido. Devuelve el estado nuevo (todo con su `subida`).
 */
export async function subirPendientes(e0: EstadoImagenes, promoId: string, conAviso: boolean, avance?: (t: string) => void): Promise<EstadoImagenes> {
  let e: EstadoImagenes = { ...e0, tarjeta: { ...e0.tarjeta }, detalle: { ...e0.detalle }, aviso: { ...e0.aviso } };
  const subirFoto = async (f: Foto, slot: string): Promise<Foto> => {
    if (f.subida || !f.fuente) return f;
    avance?.('Subiendo la foto original…');
    const red = await reducirOriginal(f.fuente, 3000, 4096);
    const r = await api<any>('promos/imagen', { method: 'POST', body: JSON.stringify({ data: red.data, slot, promo_id: promoId }) });
    return { ...f, subida: { ruta: r.ruta, url: r.url || null, ancho: r.ancho || red.ancho, alto: r.alto || red.alto } };
  };
  if (e.principal) e.principal = await subirFoto(e.principal, 'original');
  for (const k of ORDEN_ESPACIOS) {
    if (k === 'aviso' && !conAviso) continue;
    if (e[k].propia) e[k] = { ...e[k], propia: await subirFoto(e[k].propia!, `original-${k}`) };
    if (!pendiente(e, k)) continue;
    const f = fotoDe(e, k)!; const s = e[k]; const esp = ESPACIOS[k];
    const caja = cajaDe(f.fuente!, s.rec!, esp.aspecto);
    avance?.(`Subiendo «${esp.nombre}»…`);
    const { data } = await generarJpeg(f.fuente!, caja, { ancho: esp.ancho, alto: esp.alto, maxKB: esp.maxKB });
    const normal = normalDe(f.fuente!, caja);
    if (k === 'aviso') {
      const r = await api<any>('avisos/imagen', { method: 'POST', body: JSON.stringify({ data }) });
      e.aviso = { ...s, normal, subida: { ruta: r.imagen, url: r.imagen_url || null, ancho: r.ancho || esp.ancho, alto: r.alto || esp.alto, normal, de: f.subida?.ruta || null } };
    } else {
      const r = await api<any>('promos/imagen', { method: 'POST', body: JSON.stringify({ data, slot: k, promo_id: promoId }) });
      e[k] = { ...s, normal, subida: { ruta: r.ruta, url: r.url || null, ancho: r.ancho || esp.ancho, alto: r.alto || esp.alto, normal, de: f.subida?.ruta || null } };
    }
  }
  return e;
}

const btn: CSSProperties = { padding: '6px 14px', fontSize: 13 };
const ACEPTA = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif';

/** El paso 5. `conAviso` = si el aviso al empezar va prendido (la notificación sólo se sube entonces). */
export function ImagenesPromo({ valor, onCambio, promoId, disponible, motivo, conAviso, onOcupado }: {
  /** `deUsuario: false` = lo cambió la pantalla sola (abrir lo guardado): no cuenta como "cambios sin guardar". */
  valor: EstadoImagenes; onCambio: (e: EstadoImagenes, deUsuario?: boolean) => void; promoId: string; disponible: boolean; motivo: string | null; conAviso: boolean;
  onOcupado?: (x: boolean) => void;
}) {
  const [esp, setEsp] = useState<Espacio>('tarjeta');
  const [abriendo, setAbriendo] = useState(false);
  const [subiendo, setSubiendo] = useState('');
  const [error, setError] = useState('');
  const archivo = useRef<HTMLInputElement>(null);
  const destino = useRef<'principal' | Espacio>('principal');
  const ref = useRef(valor); ref.current = valor;
  const cambiar = (e: EstadoImagenes, deUsuario = true) => { ref.current = e; onCambio(e, deUsuario); };
  useEffect(() => { onOcupado?.(abriendo || !!subiendo); }, [abriendo, subiendo]);

  // Lo guardado se abre para poder recortarlo otra vez (con el marco donde quedó).
  useEffect(() => {
    let vivo = true;
    const abrir = async (f: Foto | null, nombre: string, poner: (e: EstadoImagenes, fu: Fuente | null, err: string | null) => EstadoImagenes) => {
      if (!f || f.fuente || f.cargando || f.error || !f.subida) return;
      cambiar(poner(ref.current, null, null), false);
      try { const fu = await traerFoto(f.subida.ruta, nombre); if (vivo) cambiar(poner(ref.current, fu, null), false); }
      catch (x: any) { if (vivo) cambiar(poner(ref.current, null, x?.message || 'No se pudo abrir la foto guardada.'), false); }
    };
    const conMarcos = (e: EstadoImagenes, fu: Fuente, propiaDe: Espacio | null): EstadoImagenes => {
      const n = { ...e };
      for (const k of ORDEN_ESPACIOS) {
        if (propiaDe ? k !== propiaDe : !!n[k].propia) continue;
        if (n[k].rec) continue;
        n[k] = { ...n[k], rec: recDeNormal(fu, n[k].subida?.normal || n[k].normal, ESPACIOS[k].aspecto) };
      }
      return n;
    };
    abrir(valor.principal, 'foto principal', (e, fu, err) => {
      const p: Foto = { ...e.principal!, cargando: !fu && !err, fuente: fu, error: err };
      return fu ? conMarcos({ ...e, principal: p }, fu, null) : { ...e, principal: p };
    });
    for (const k of ORDEN_ESPACIOS) {
      abrir(valor[k].propia, `foto de ${ESPACIOS[k].nombre}`, (e, fu, err) => {
        const p: Foto = { ...e[k].propia!, cargando: !fu && !err, fuente: fu, error: err };
        const n = { ...e, [k]: { ...e[k], propia: p } } as EstadoImagenes;
        return fu ? conMarcos(n, fu, k) : n;
      });
    }
    return () => { vivo = false; };
  }, [valor.principal?.subida?.ruta, valor.tarjeta.propia?.subida?.ruta, valor.detalle.propia?.subida?.ruta, valor.aviso.propia?.subida?.ruta]);

  async function elegido(file: File | undefined) {
    if (!file) return;
    setError(''); setAbriendo(true);
    try {
      const fu0 = await abrirArchivo(file);
      if (fu0.w < 320 || fu0.h < 320) throw new Error(`Esa foto mide ${fu0.w} × ${fu0.h}: tiene que medir al menos 320 × 320 (mejor ${RECOMENDADO} px de ancho o más).`);
      // Se trabaja con la foto ya reducida (lado ≤ 3000): es la misma que se guarda, así el marco cuadra al volver a abrirla.
      const red = await reducirOriginal(fu0, 3000, 4096);
      const fu: Fuente = { src: red.data, w: red.ancho, h: red.alto, nombre: fu0.nombre, ...({ ancho0: fu0.w } as any) };
      const foto: Foto = { fuente: fu, subida: null };
      const e = { ...ref.current };
      if (destino.current === 'principal') {
        e.principal = foto;
        for (const k of ORDEN_ESPACIOS) if (!e[k].propia) e[k] = { ...e[k], rec: recInicial(fu), normal: null };
      } else {
        const k = destino.current;
        e[k] = { ...e[k], propia: foto, rec: recInicial(fu), normal: null };
        setEsp(k);
      }
      cambiar(e);
    } catch (x: any) { setError(x?.message || 'No se pudo abrir esa foto.'); }
    finally { setAbriendo(false); if (archivo.current) archivo.current.value = ''; }
  }
  const elegir = (d: 'principal' | Espacio) => { destino.current = d; archivo.current?.click(); };
  async function usar() {
    setError('');
    try { cambiar(await subirPendientes(ref.current, promoId, conAviso, setSubiendo)); }
    catch (x) { setError(textoDeError(x, 'No se pudo subir la imagen.')); }
    finally { setSubiendo(''); }
  }
  function quitarPrincipal() {
    const e = { ...ref.current, principal: null };
    for (const k of ORDEN_ESPACIOS) if (!e[k].propia) e[k] = espacioVacio();
    cambiar(e);
  }
  function volverPrincipal(k: Espacio) {
    const e = { ...ref.current };
    const f = e.principal?.fuente;
    e[k] = { ...e[k], propia: null, rec: f ? recInicial(f) : null, normal: null };
    cambiar(e);
  }

  const p = valor.principal;
  const s = valor[esp]; const info = ESPACIOS[esp];
  const foto = fotoDe(valor, esp);
  const fu = foto?.fuente || null;
  const caja = fu && s.rec ? cajaDe(fu, s.rec, info.aspecto) : null;
  const ancho0 = (fu as any)?.ancho0 || fu?.w || 0;
  const pend = hayPendientes(valor, conAviso);
  const estadoEsp = (k: Espacio) => {
    const f = fotoDe(valor, k);
    if (!f) return valor[k].subida ? 'guardada' : 'sin imagen';
    if (pendiente(valor, k) || (valor[k].propia?.fuente && !valor[k].propia!.subida)) return k === 'aviso' && !conAviso ? 'sólo con aviso' : 'sin subir';
    return valor[k].subida ? 'guardada' : f.cargando ? 'abriendo…' : 'sin subir';
  };
  return (
    <div style={{ display: 'grid', gap: 12 }} data-img-promo>
      <input ref={archivo} type="file" accept={ACEPTA} style={{ display: 'none' }} onChange={(e) => elegido(e.target.files?.[0])} data-img-archivo aria-label="Elegir foto" />
      {!disponible && <span style={{ fontSize: 12.5, color: 'var(--warning)' }} data-img-no>{motivo || 'Hoy no se pueden subir imágenes.'} La promoción se guarda igual: en la app la tarjeta sale con el degradado dorado de la marca.</span>}
      <p className="muted" style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5 }} data-img-tamanos>
        Para que se vea nítida en todos lados, sube una foto de al menos {RECOMENDADO.toLocaleString('es-MX')} px de ancho. Tarjeta 1280 × 720 · Al abrirla 1080 × 1350 · Notificación 1440 × 720. JPG, PNG, WebP o HEIC (la del iPhone).
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
        {!p && <button type="button" className="btn-primary" style={btn} disabled={!disponible || abriendo} onClick={() => elegir('principal')} data-img-elegir>{abriendo ? 'Abriendo…' : 'Elegir una foto'}</button>}
        {p && <>
          <span data-img-principal style={{ fontSize: 12.5 }}><strong>Foto principal</strong>{' '}
            <span className="muted">{p.fuente ? `${(p.fuente as any).ancho0 && (p.fuente as any).ancho0 !== p.fuente.w ? `${(p.fuente as any).ancho0} px de ancho (se guarda de ${p.fuente.w} × ${p.fuente.h})` : `${p.fuente.w} × ${p.fuente.h}`}` : p.subida ? `${p.subida.ancho} × ${p.subida.alto} · guardada` : ''}{p.cargando ? ' · abriendo…' : ''}</span></span>
          <button type="button" className="btn-ghost" style={btn} disabled={!disponible || abriendo} onClick={() => elegir('principal')} data-img-cambiar>{abriendo ? 'Abriendo…' : 'Cambiar foto'}</button>
          <button type="button" className="btn-ghost" style={{ ...btn, color: 'var(--danger)' }} onClick={quitarPrincipal} data-img-quitar>Quitar foto</button>
        </>}
      </div>
      {p?.error && <span style={{ fontSize: 12.5, color: 'var(--danger)' }} role="alert" data-img-error-guardada>{p.error} Puedes elegir la foto otra vez.</span>}
      {ancho0 > 0 && ancho0 < RECOMENDADO && <span style={{ fontSize: 12.5, color: 'var(--warning)' }} data-img-chica>Esta foto mide {ancho0} px de ancho: se va a ver, pero en un teléfono grande puede verse un poco borrosa (lo ideal es {RECOMENDADO.toLocaleString('es-MX')} px o más).</span>}

      {(p || ORDEN_ESPACIOS.some((k) => valor[k].propia || valor[k].subida)) && <>
        <div role="tablist" aria-label="Espacio de la imagen" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {ORDEN_ESPACIOS.map((k) => <button key={k} type="button" role="tab" aria-selected={esp === k} data-espacio-tab={k} onClick={() => setEsp(k)}
            className={esp === k ? 'btn-primary' : 'btn-ghost'} style={{ ...btn, display: 'grid', gap: 1, textAlign: 'left', lineHeight: 1.25 }}>
            <span>{ESPACIOS[k].nombre} {ESPACIOS[k].relacion}</span>
            <span style={{ fontSize: 11, opacity: 0.85 }} data-espacio-estado={k}>{estadoEsp(k)}</span>
          </button>)}
        </div>
        <div style={{ display: 'grid', gap: 8 }} data-espacio={esp}>
          <span className="muted" style={{ fontSize: 12.5 }}>{info.donde} · sale de {info.ancho} × {info.alto}{esp === 'aviso' && !conAviso ? ' · sólo se usa si mandas el aviso al empezar (paso 6)' : ''}{esp === 'aviso' ? '. En iPhone la notificación no enseña imagen.' : ''}</span>
          {fu && s.rec ? <Recortador fuente={fu} rec={s.rec} onRec={(r) => cambiar({ ...ref.current, [esp]: { ...ref.current[esp], rec: r } } as EstadoImagenes)} aspecto={info.aspecto} zona={info.zona} espacio={esp}
            maxAlto={typeof window !== 'undefined' && window.innerWidth < 600 ? 260 : 360} />
            : foto?.cargando ? <span className="muted" style={{ fontSize: 12.5 }}>Abriendo la foto guardada…</span>
            : !foto && s.subida ? <span className="muted" style={{ fontSize: 12.5 }}>Este espacio tiene una imagen guardada, pero no su foto original: para recortarlo, elige una foto.</span>
            : null}
          {caja && caja.w < info.ancho * 0.75 && <span style={{ fontSize: 12, color: 'var(--warning)' }} data-img-borrosa>La parte elegida mide {Math.round(caja.w)} px de ancho y este espacio sale de {info.ancho}: en un teléfono grande puede verse borrosa. Aleja un poco o usa una foto más grande.</span>}
          {valor[esp].propia?.error && <span style={{ fontSize: 12.5, color: 'var(--danger)' }} role="alert">{valor[esp].propia!.error}</span>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn-ghost" style={btn} disabled={!disponible || abriendo} onClick={() => elegir(esp)} data-img-otra>{valor[esp].propia ? 'Cambiar la imagen de aquí' : 'Usar otra imagen aquí'}</button>
            {valor[esp].propia && p && <button type="button" className="btn-ghost" style={btn} onClick={() => volverPrincipal(esp)} data-img-volver>Volver a la foto principal</button>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="btn-primary" style={btn} disabled={!disponible || !!subiendo || !pend} onClick={usar} data-img-usar>{subiendo ? 'Subiendo…' : 'Usar esta imagen'}</button>
          <span className="muted" style={{ fontSize: 12.5 }} data-img-pendiente>{subiendo || (pend ? 'Hay recortes sin subir: se suben con «Usar esta imagen» o al guardar la promoción.' : 'Todo está subido.')}</span>
        </div>
      </>}
      {error && <span style={{ fontSize: 12.5, color: 'var(--danger)' }} role="alert" data-img-error>{error}</span>}
    </div>
  );
}
